//source/app.tsx
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "./app.css";
import {
  fetchLeagueLeaderboard,
  fetchTeamsLeaderboardRanking,
} from "./api/leaderboard";
import { LeaderboardRankingItem } from "./api/types";
import Loading from "./Loading";
import Header from "./Header";
import LeagueTable from "./LeagueTable";
import LEAGUES, { League } from "./constants/leagues";
import { Team } from "./types/team";
import { deserializeLeaderboard } from "./utils/deserializeLeaderboard";
import { useSwipe } from "./hooks/useSwipe";

const App = () => {
  const [loading, setLoading] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState<League>(LEAGUES[0]);
  const [leagueData, setLeagueData] = useState<Record<string, Team[]>>({});

  const search = window.location.search;
  const params = new URLSearchParams(search);
  const campaignGuid = params.get("campaignGuid");
  const ranking = params.get("ranking");

  useEffect(() => {
    if (campaignGuid) {
      handleLoadLeague(campaignGuid, ranking);
    }
  }, [campaignGuid, ranking]);

  const handleLoadLeague = async (campaign: string, ranking: string | null) => {
    setLoading(true);

    try {
      // Load Rankings
      let teamsRanking: LeaderboardRankingItem[] | null = null;
      if (ranking === "true") {
        teamsRanking = await fetchTeamsLeaderboardRanking({ campaign });
      }

      // Loading first league
      let result = await loadLeague(teamsRanking, campaign, LEAGUES[0].key);

      // Show UI
      setLoading(false);

      // Prefetching remaining leagues
      for (const league of LEAGUES.slice(1)) {
        if (!result.hasMoreResults) {
          break;
        }
        result = await loadLeague(
          teamsRanking,
          campaign,
          league.key,
          result.nextPageToken,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const loadLeague = async (
    rankings: LeaderboardRankingItem[] | null,
    campaign: string,
    key: League["key"],
    token?: string,
  ) => {
    const data = await fetchLeagueLeaderboard({ campaign }, token);
    const results = data.results ?? [];

    setLeagueData((prev) => ({
      ...prev,
      [key]: results.map((item, index) =>
        deserializeLeaderboard(item, index, rankings),
      ),
    }));

    return {
      nextPageToken: data.next,
      hasMoreResults: results.length >= 20,
    };
  };

  const currentIndex = LEAGUES.findIndex(
    (league) => league.key === selectedLeague.key,
  );

  const swipeHandlers = useSwipe({
    onSwipeLeft: () => {
      if (currentIndex < LEAGUES.length - 1) {
        setSelectedLeague(LEAGUES[currentIndex + 1]);
      }
    },
    onSwipeRight: () => {
      if (currentIndex > 0) {
        setSelectedLeague(LEAGUES[currentIndex - 1]);
      }
    },
  });

  if (loading) {
    return (
      <div className="loading-container">
        <Loading size={2} />
      </div>
    );
  }

  const teams = leagueData[selectedLeague.key] ?? [];

  return (
    <div {...swipeHandlers}>
      <Header
        navigation={LEAGUES}
        selected={selectedLeague.key}
        onSelect={(key) => {
          const league = LEAGUES.find((league) => league.key === key);
          if (league) {
            setSelectedLeague(league);
          }
        }}
      />
      <LeagueTable league={selectedLeague} teams={teams} />
    </div>
  );
};

const container = document.getElementById("app");

if (!container) {
  throw new Error('Root element with id "app" not found.');
}

const root = ReactDOM.createRoot(container);

root.render(<App />);
